import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../config/api';
import './KYCManagement.css';

const KYCManagement = ({ user, onShowNotification }) => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    loadAgents();
  }, [currentPage, searchTerm, kycStatusFilter]);

  const loadAgents = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        kycStatus: kycStatusFilter
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc/agents?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAgents(result.agents || []);
          setPagination(result.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        }
      }
    } catch (error) {
      console.error('Error loading agents:', error);
      if (onShowNotification) {
        onShowNotification('Error loading agents. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadAgentDetails = async (agentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc/agents/${agentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAgentDetails(result.agent);
          setDocuments(result.documents || []);
          setSelectedAgent(agentId);
        }
      }
    } catch (error) {
      console.error('Error loading agent details:', error);
      if (onShowNotification) {
        onShowNotification('Error loading agent details. Please try again.', 'error');
      }
    }
  };

  const handleReviewAgent = async () => {
    if (!selectedAgent || !reviewStatus) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc/agents/${selectedAgent}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kyc_status: reviewStatus,
          review_notes: reviewNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          if (onShowNotification) {
            onShowNotification(`Agent KYC ${reviewStatus} successfully!`, 'success');
          }
          setShowReviewModal(false);
          setReviewNotes('');
          loadAgents();
          loadAgentDetails(selectedAgent);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update KYC status' }));
        throw new Error(errorData.error || 'Failed to update KYC status');
      }
    } catch (error) {
      console.error('Error reviewing agent:', error);
      if (onShowNotification) {
        onShowNotification(error.message || 'Error updating KYC status. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDocument = async (docId, documentName) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc/documents/${docId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = documentName || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      if (onShowNotification) {
        onShowNotification('Error viewing document. Please try again.', 'error');
      }
    }
  };

  const getStatusBadge = (status) => {
    // Normalize status: treat pending_review as pending (only show Pending and Approved)
    const normalizedStatus = (status === 'pending_review' || status === 'pending') ? 'pending' : 
                            (status === 'approved' ? 'approved' : 'pending');
    
    const statusMap = {
      pending: { bg: '#fff3cd', color: '#856404', text: 'Pending' },
      approved: { bg: '#d4edda', color: '#155724', text: 'Approved' }
    };
    
    const config = statusMap[normalizedStatus] || statusMap.pending;
    return (
      <span style={{
        background: config.bg,
        color: config.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="kyc-management-container">
      <div className="kyc-header">
        <div className="header-left">
          <h2>🔐 KYC Verification Management</h2>
          <p>Review and verify agent KYC documents and status</p>
        </div>
      </div>

      {/* Filter Bar - Prominent Top Section */}
      <div className="kyc-filter-bar">
        <div className="filter-bar-content">
          <div className="filter-group">
            <label htmlFor="search-agents" className="filter-label">
              <span className="filter-icon">🔍</span>
              Search Agents
            </label>
            <input
              type="text"
              id="search-agents"
              placeholder="Search by name, IRA number, email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="status-filter" className="filter-label">
              <span className="filter-icon">📊</span>
              Filter by Status
            </label>
            <select
              id="status-filter"
              value={kycStatusFilter}
              onChange={(e) => {
                setKycStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          {(searchTerm || kycStatusFilter !== 'all') && (
            <button
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setKycStatusFilter('all');
                setCurrentPage(1);
              }}
              title="Clear all filters"
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="kyc-content">
        <div className="agents-list-panel">
          <h3>Agents ({pagination.total})</h3>
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading agents...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="no-data">No agents found</div>
          ) : (
            <div className="agents-list">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className={`agent-card ${selectedAgent === agent.id ? 'selected' : ''}`}
                  onClick={() => loadAgentDetails(agent.id)}
                >
                  <div className="agent-card-header">
                    <h4>{agent.agency_name}</h4>
                    {getStatusBadge(agent.kyc_status)}
                  </div>
                  <div className="agent-card-body">
                    <div className="agent-info-row">
                      <span className="label">IRA Number:</span>
                      <span className="value">{agent.ira_number}</span>
                    </div>
                    {agent.full_names && (
                      <div className="agent-info-row">
                        <span className="label">Name:</span>
                        <span className="value">{agent.full_names}</span>
                      </div>
                    )}
                    {agent.id_number && (
                      <div className="agent-info-row">
                        <span className="label">ID Number:</span>
                        <span className="value">{agent.id_number}</span>
                      </div>
                    )}
                    {agent.kra_pin && (
                      <div className="agent-info-row">
                        <span className="label">KRA PIN:</span>
                        <span className="value">{agent.kra_pin}</span>
                      </div>
                    )}
                    <div className="agent-info-row">
                      <span className="label">Documents:</span>
                      <span className="value">
                        {agent.approved_documents || 0} / {agent.total_documents || 0} approved
                      </span>
                    </div>
                    {agent.kyc_reviewed_at && (
                      <div className="agent-info-row">
                        <span className="label">Reviewed:</span>
                        <span className="value">
                          {new Date(agent.kyc_reviewed_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>Page {currentPage} of {pagination.totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="agent-details-panel">
          {selectedAgent && agentDetails ? (
            <>
              <div className="agent-details-header">
                <h3>{agentDetails.agency_name}</h3>
                <button
                  className="review-btn"
                  onClick={() => setShowReviewModal(true)}
                  disabled={agentDetails.kyc_status === 'approved'}
                >
                  {agentDetails.kyc_status === 'approved' ? '✅ Verified' : '🔍 Review KYC'}
                </button>
              </div>

              <div className="agent-details-content">
                <div className="details-section">
                  <h4>Agent Information</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">IRA Number:</span>
                      <span className="value">{agentDetails.ira_number}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Username:</span>
                      <span className="value">{agentDetails.username}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span className="value">{agentDetails.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone:</span>
                      <span className="value">{agentDetails.phone || 'N/A'}</span>
                    </div>
                    {agentDetails.full_names && (
                      <div className="detail-item">
                        <span className="label">Full Name:</span>
                        <span className="value">{agentDetails.full_names}</span>
                      </div>
                    )}
                    {agentDetails.id_number && (
                      <div className="detail-item">
                        <span className="label">ID Number:</span>
                        <span className="value">{agentDetails.id_number}</span>
                      </div>
                    )}
                    {agentDetails.kra_pin && (
                      <div className="detail-item">
                        <span className="label">KRA PIN:</span>
                        <span className="value">{agentDetails.kra_pin}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="label">KYC Status:</span>
                      <span className="value">{getStatusBadge(agentDetails.kyc_status)}</span>
                    </div>
                  </div>
                </div>

                <div className="documents-section">
                  <h4>KYC Documents</h4>
                  {documents.length === 0 ? (
                    <div className="no-data">No documents uploaded</div>
                  ) : (
                    <div className="documents-list">
                      {documents.map((doc) => (
                        <div key={doc.id} className="document-card">
                          <div className="document-header">
                            <span className="document-type">
                              {doc.document_type === 'ira_licence' && '📜'}
                              {doc.document_type === 'certificate_of_incorporation' && '🏢'}
                              {doc.document_type === 'certificate_of_proficiency' && '🎓'}
                              {' '}
                              {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            {getStatusBadge(doc.review_status)}
                          </div>
                          <div className="document-body">
                            <div className="document-info">
                              <span className="document-name">{doc.document_name}</span>
                              <span className="document-size">
                                {(doc.file_size / 1024).toFixed(2)} KB
                              </span>
                            </div>
                            {doc.is_flagged && doc.flag_reason && (
                              <div className="flag-warning">
                                ⚠️ Flagged: {doc.flag_reason}
                              </div>
                            )}
                            {doc.review_notes && (
                              <div className="review-notes">
                                Notes: {doc.review_notes}
                              </div>
                            )}
                            <div className="document-actions">
                              <button
                                onClick={() => handleViewDocument(doc.id, doc.document_name)}
                                className="view-doc-btn"
                              >
                                👁️ View Document
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h3>Select an agent to view details</h3>
              <p>Click on an agent from the list to view their KYC information and documents</p>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Agent KYC</h3>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>KYC Status:</label>
                <select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="form-select"
                >
                  <option value="approved">✅ Approved</option>
                  <option value="rejected">❌ Rejected</option>
                  <option value="flagged">⚠️ Flagged</option>
                </select>
              </div>
              <div className="form-group">
                <label>Review Notes:</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add review notes (optional)"
                  rows="4"
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowReviewModal(false);
                  setReviewNotes('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleReviewAgent}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCManagement;

